import { ApiError, DEMO_ACCOUNTS, type OtpChallenge, type Session } from '@klukka/client';
import { ta, type AdminLang } from '@klukka/core';
import { Ic } from '@klukka/ui';
import { useState } from 'react';
import { api, showDemoHints } from './api';

/** Administrator sign-in. Same kennitala + OTP path as the workers use. */
export function Login({ lang, onLang, onSignedIn }: {
  lang: AdminLang;
  onLang: (l: AdminLang) => void;
  onSignedIn: (s: Session) => void;
}) {
  const tr = (k: Parameters<typeof ta>[1]) => ta(lang, k);
  const [step, setStep] = useState<'kt' | 'otp'>('kt');
  const [kt, setKt] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submitKt = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const ch = await api.requestOtp(kt, 'employer');
      setChallenge(ch);
      if (ch.firstLogin) {
        onSignedIn(await api.completeFirstLogin(ch.challengeId));
        return;
      }
      setStep('otp');
    } catch (e) {
      setErr(e instanceof ApiError && e.code === 'account_not_found' ? tr('ktNotFound') : tr('saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async () => {
    if (busy || !challenge) return;
    setBusy(true);
    setErr(null);
    try {
      onSignedIn(await api.verifyOtp(challenge.challengeId, code));
    } catch {
      setErr(tr('wrongCode'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login">
        {/*
          "Klukka-Stjórnborð" against the phone app's "Klukka-app": an
          administrator who lands on the wrong hostname should see it in the
          name, not work it out from the layout. The card no longer repeats the
          line above it.
        */}
        <div className="login-brand">
          <Ic.chart size={40} sw={1.8} />
          <b>{tr('brand')}</b>
          <span>{tr('loginTitle')}</span>
        </div>

        {step === 'kt' ? (
          <div className="login-box">
            <label className="fld">
              kt.
              <input
                className={`inp${err ? ' err' : ''}`}
                inputMode="numeric"
                autoComplete="username"
                placeholder="000000-0000"
                value={kt}
                onChange={(e) => { setKt(e.target.value); setErr(null); }}
                onKeyDown={(e) => e.key === 'Enter' && void submitKt()}
              />
            </label>
            {err ? <span className="err-msg">{err}</span> : null}
            <button className="btn-main" style={{ width: '100%', marginTop: 14 }} disabled={busy} onClick={() => void submitKt()}>
              {busy ? tr('loading') : tr('continueBtn')}
            </button>
          </div>
        ) : (
          <div className="login-box">
            <div className="card-title">{tr('otpSent')}</div>
            <div className="dimtx" style={{ fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Ic.phone size={14} /> {challenge?.maskedPhone}
            </div>
            <label className="fld">
              <input
                className={`inp otp${err ? ' err' : ''}`}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                placeholder="0000"
                value={code}
                onChange={(e) => { setCode(e.target.value); setErr(null); }}
                onKeyDown={(e) => e.key === 'Enter' && void submitOtp()}
              />
            </label>
            {err ? <span className="err-msg">{err}</span> : null}
            <button className="btn-main" style={{ width: '100%', marginTop: 14 }} disabled={busy} onClick={() => void submitOtp()}>
              {busy ? tr('loading') : tr('continueBtn')}
            </button>
            {challenge?.devCode ? <div className="demo-otp">DEMO · SMS: <b>{challenge.devCode}</b></div> : null}
          </div>
        )}

        <div className="seg3" style={{ maxWidth: 160 }}>
          <button className={lang === 'is' ? 'on' : ''} onClick={() => onLang('is')}>IS</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => onLang('en')}>EN</button>
        </div>

        {showDemoHints && step === 'kt' ? (
          <div className="login-hints">
            <b>Demo</b> — tap a kennitala to fill it in:
            {DEMO_ACCOUNTS.admins.map((a) => (
              <div key={a.kennitala}>
                <button onClick={() => { setKt(a.kennitala); setErr(null); }}>{a.kennitala}</button>
                {' '}· {a.name} <span className="dimtx">({a.company})</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
